import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useApplication } from '@/Components/ApplicationProvider';
import Icon from '@/Components/Icon/Icon';
import Spinner from '@/Components/Spinner/Spinner';
import { isDesktopApplication } from '@/Utils';
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { classNames, Platform, PrefKey } from '@standardnotes/snjs';
import { $getNodeByKey, CLICK_COMMAND, COMMAND_PRIORITY_LOW } from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { $createFileNode } from '../EncryptedFilePlugin/Nodes/FileUtils';
import { isIOS } from '@standardnotes/ui-services';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import usePreference from '@/Hooks/usePreference';
import { getCSSValueFromAlignment, ImageAlignmentOptions } from '@/Components/FilePreview/ImageAlignmentOptions';
import { getOverflows } from '@/Components/Popover/Utils/Collisions';
import { c } from 'ttag';
const RemoteImageComponent = ({ className, src, alt, node, format, nodeKey, setFormat }) => {
    const application = useApplication();
    const [editor] = useLexicalComposerContext();
    const [didImageLoad, setDidImageLoad] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const fetchAndUploadImage = useCallback(async () => {
        setIsSaving(true);
        try {
            const response = await fetch(src);
            if (!response.ok) {
                return;
            }
            const blob = await response.blob();
            const file = new File([blob], src, { type: blob.type });
            const { filesController, linkingController } = application;
            const uploadedFile = await filesController.uploadNewFile(file, { showToast: false });
            if (!uploadedFile) {
                return;
            }
            editor.update(() => {
                const fileNode = $createFileNode(uploadedFile.uuid);
                node.replace(fileNode);
            });
            void linkingController.linkItemToSelectedItem(uploadedFile);
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setIsSaving(false);
        }
    }, [application, editor, node, src]);
    const isBase64OrDataUrl = src.startsWith('data:');
    const canShowSaveButton = application.isNativeMobileWeb() || isDesktopApplication() || isBase64OrDataUrl;
    const ref = useRef(null);
    const [isSelected, setSelected] = useLexicalNodeSelection(nodeKey);
    useEffect(() => {
        return editor.registerCommand(CLICK_COMMAND, (event) => {
            var _a, _b;
            if ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) {
                event.preventDefault();
                (_b = $getNodeByKey(nodeKey)) === null || _b === void 0 ? void 0 : _b.selectEnd();
                setTimeout(() => {
                    setSelected(!isSelected);
                });
                return true;
            }
            return false;
        }, COMMAND_PRIORITY_LOW);
    }, [editor, isSelected, nodeKey, setSelected]);
    const changeAlignment = useCallback((format) => {
        editor.update(() => {
            setFormat(format);
        });
    }, [editor, setFormat]);
    const defaultSuperImageAlignment = usePreference(PrefKey.SuperNoteImageAlignment);
    const finalAlignment = format || defaultSuperImageAlignment;
    const alignItems = getCSSValueFromAlignment(finalAlignment);
    return (_jsx(BlockWithAlignableContents, { className: className, format: format, nodeKey: nodeKey, children: _jsxs("div", { ref: ref, className: "group relative flex min-h-[2rem] flex-col gap-2.5", style: { alignItems }, onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
            }, children: [_jsx("img", { alt: alt, src: src, onLoad: () => {
                        setDidImageLoad(true);
                    } }), _jsx("div", { className: "invisible absolute bottom-full left-1/2 z-10 w-max -translate-x-1/2 px-1 pb-1 focus-within:visible group-hover:visible [.embedBlockFocused_&]:visible", ref: (popover) => {
                        const editorRoot = editor.getRootElement();
                        if (!popover || !editorRoot) {
                            return;
                        }
                        const editorRootRect = editorRoot.getBoundingClientRect();
                        const popoverRect = popover.getBoundingClientRect();
                        const overflows = getOverflows(popoverRect, editorRootRect);
                        if (overflows.top > 0) {
                            popover.style.setProperty('--tw-translate-y', `${overflows.top}px`);
                        }
                    }, children: _jsx("div", { className: "flex gap-1 rounded border border-border bg-default px-1 py-0.5", children: _jsx(ImageAlignmentOptions, { alignment: finalAlignment, changeAlignment: changeAlignment }) }) }), didImageLoad && canShowSaveButton && (_jsx("button", { className: classNames('flex items-center gap-2.5 rounded border border-border bg-default px-2.5 py-1.5', !isSaving && 'hover:bg-info hover:text-info-contrast'), onClick: () => {
                        const isIOSPlatform = application.platform === Platform.Ios || isIOS();
                        if (isIOSPlatform && document.activeElement) {
                            ;
                            document.activeElement.blur();
                        }
                        fetchAndUploadImage().catch(console.error);
                    }, disabled: isSaving, children: isSaving ? (_jsxs(_Fragment, { children: [_jsx(Spinner, { className: "h-4 w-4" }), c('B4.Notes.EditingUI.Status').t `Saving...`] })) : (_jsxs(_Fragment, { children: [_jsx(Icon, { type: "download" }), c('B4.Notes.EditingUI.Action').t `Save image to Files`] })) }))] }) }));
};
export default RemoteImageComponent;
